<script setup lang="ts">
/**
 * Who can see this source's data — part by part.
 *
 * A source is rarely one thing: SharePoint here is an open offering
 * catalogue, a Bid Office library only the bid team may see, and two vendor
 * knowledge bases. A row per assistant squashed those into one word ("the
 * loosest wins"), so Bid Writing read as "all of it" and Account
 * Intelligence as "public" with no way to tell which part either meant.
 * Now each PART states its rule once, and under it each assistant that
 * reads it says who, through that assistant, actually sees it:
 *
 *   - open part → anyone who can open the assistant;
 *   - restricted part, and the assistant's own group is the part's group →
 *     everyone who can open the assistant (the group decision is the whole
 *     access decision — this is where the data owner confirms);
 *   - restricted part, other group → only teams cleared for that data class
 *     (ADR 0064), plus admins. Entra members are narrowed out there.
 *
 * Tables follow their own gate: rows per person, or a group list.
 *
 * This is where the data owner's second act lives, beside the first. The
 * agreement (above this on the page) is signed once per source. The
 * confirmation here is per assistant: for an assistant where being in a
 * group is the whole access decision, the data owner confirms that
 * everyone in that group already has full access in the source system.
 * They stay two records on purpose — groups change far more often than
 * agreements do, and a confirmation must go stale when its group widens —
 * but a data owner can now do both on one page, and "confirm all" does
 * every assistant at once.
 */
import { vWrite } from "../admin-read-only";
import { computed, ref } from "vue";
import {
  AdminError,
  type ClearanceClass,
  type DataSource,
  type GovernanceAssistant,
  type Team,
  attestGovernanceAccess,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { adminLink, groupLabel } from "../utils/vocabulary";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{
  source: DataSource;
  names: Record<string, string>;
  /** slug → governance row; empty while loading or when unavailable. */
  gov: Map<string, GovernanceAssistant>;
  /** Teams and data classes, for who is cleared for a restricted part. */
  teams: Team[];
  classes: ClearanceClass[];
  devUser: string;
}>();
const emit = defineEmits<{
  (e: "gov-updated", a: GovernanceAssistant): void;
}>();

const toast = useToast();
const { confirm } = useConfirm();

type Confirmation =
  | { kind: "not-needed" }
  | { kind: "unknown" }
  | { kind: "missing" }
  | { kind: "stale"; who: string; when: string }
  | { kind: "current"; who: string; when: string };

function confirmation(a: GovernanceAssistant | undefined): Confirmation {
  if (!a) return { kind: "unknown" };
  if (a.access_class?.key !== "group_gated" || !a.acl_scope.length)
    return { kind: "not-needed" };
  // This page's confirmation is for THIS source only; the assistant's other
  // sources are confirmed by their own owners on their own pages.
  const at =
    a.access_attestations?.find((r) => r.source_key === props.source.key) ??
    (a.access_attestations ? undefined : a.access_attestation);
  if (!at?.attested) return { kind: "missing" };
  const when = (at.recorded_at ?? "").slice(0, 10);
  return at.current
    ? { kind: "current", who: at.data_owner, when }
    : { kind: "stale", who: at.data_owner, when };
}

/** Who can open an assistant, in words. */
function openers(a: GovernanceAssistant | undefined): string {
  if (!a) return "";
  const who = [
    ...a.acl_scope.map(groupLabel),
    ...(a.teams ?? []).map((t) => `team ${t.name}`),
  ];
  return who.length ? who.join(", ") : "anyone signed in";
}

type Reader = {
  slug: string;
  name: string;
  opens: string;
  /** Through this assistant, who sees this part. */
  sees: string;
  /** The whole group sees it: the data owner confirms this assistant. */
  confirm: boolean;
  conf: Confirmation;
};

type Part = {
  key: string;
  kind: "Documents" | "Table";
  label: string;
  rule: "open" | "group" | "mirrored" | "assigned" | "own" | "undeclared";
  cls: ClearanceClass | null;
  tokens: string[];
  readers: Reader[];
};

const RULE: Record<Part["rule"], string> = {
  open: "Open — anyone who can open an assistant that reads it",
  group: "Restricted",
  mirrored: "Per person — each sees what they can see in the source system",
  assigned: "Per person — each sees the rows they have been given here",
  own: "Per person — each sees only their own",
  undeclared: "Not declared — nobody has recorded how this part is gated",
};

function clearedTeams(token: string): string[] {
  return props.teams
    .filter((t) => t.clearances.some((c) => c.token === token))
    .map((t) => `${t.name} (${t.members.length})`);
}

function reader(slug: string, part: Omit<Part, "readers">): Reader {
  const a = props.gov.get(slug);
  const name = props.names[slug] || a?.name || slug;
  const opens = openers(a);
  if (part.rule !== "group")
    return {
      slug,
      name,
      opens,
      sees: "",
      confirm: false,
      conf: { kind: "not-needed" },
    };
  const own = (a?.acl_scope ?? []).some((g) => part.tokens.includes(g));
  if (own)
    return {
      slug,
      name,
      opens,
      sees: "everyone who can open it",
      confirm: true,
      conf: confirmation(a),
    };
  const cleared = part.tokens.flatMap(clearedTeams);
  return {
    slug,
    name,
    opens,
    sees: cleared.length
      ? `only teams cleared for it: ${cleared.join(", ")}`
      : "only admins — no team is cleared for it yet",
    confirm: false,
    conf: { kind: "not-needed" },
  };
}

const parts = computed<Part[]>(() => {
  const out: Part[] = [];
  for (const c of props.source.corpora) {
    if (c.status === "reserved") continue;
    const cls = props.classes.find((k) => k.tags.includes(c.tag)) ?? null;
    const rule = (
      ["open", "group", "mirrored", "assigned", "own"].includes(c.enforcement)
        ? c.enforcement
        : "undeclared"
    ) as Part["rule"];
    const base = {
      key: `c:${c.tag}`,
      kind: "Documents" as const,
      label: c.label || `content tagged “${c.tag}”`,
      rule,
      cls,
      tokens: cls ? [cls.token] : [],
    };
    out.push({ ...base, readers: c.assistants.map((x) => reader(x, base)) });
  }
  for (const t of props.source.tables) {
    const tokens = t.access_groups ?? [];
    const cls = props.classes.find((k) => k.tables.includes(t.table)) ?? null;
    const base = {
      key: `t:${t.table}`,
      kind: "Table" as const,
      label: t.table,
      rule: (t.row_security
        ? "assigned"
        : tokens.length
          ? "group"
          : "undeclared") as Part["rule"],
      cls,
      tokens,
    };
    out.push({ ...base, readers: t.assistants.map((x) => reader(x, base)) });
  }
  return out;
});

/** Assistants where a whole group sees a restricted part: one confirmation
 *  each, however many parts they read. */
const rows = computed(() => {
  const seen = new Map<string, Reader>();
  for (const p of parts.value)
    for (const r of p.readers) if (r.confirm) seen.set(r.slug, r);
  return [...seen.values()];
});

const needing = computed(() =>
  rows.value.filter(
    (r) => r.conf.kind === "missing" || r.conf.kind === "stale",
  ),
);

function restrictedTo(p: Part): string {
  const who = p.cls
    ? p.cls.label
    : p.tokens.map(groupLabel).join(", ") || "one group";
  return `Restricted — ${who}`;
}

// ── Record the data owner's confirmation ─────────────────────────────────

const formFor = ref<string[] | null>(null);
const owner = ref("");
const note = ref("");
const saving = ref(false);

function start(slugs: string[]): void {
  formFor.value = slugs;
  // Responsibilities names the owner (else the registry's text); the
  // agreement is the usual evidence.
  owner.value =
    props.source.data_owner_people?.[0]?.name ||
    props.source.owner.split(";")[0].trim();
  const id = props.source.agreement_meta?.id;
  note.value = id ? `Confirmed with data access agreement ${id}` : "";
}

/** The restricted parts of this source the chosen assistants read. */
const confirmedParts = computed(() => {
  const slugs = new Set(formFor.value ?? []);
  return parts.value
    .filter(
      (p) => p.rule === "group" && p.readers.some((r) => slugs.has(r.slug)),
    )
    .map((p) => p.label);
});

const statement = computed(() => {
  const slugs = formFor.value ?? [];
  const who = [...new Set(slugs.map((s) => openers(props.gov.get(s))))].join(
    "; ",
  );
  const what = confirmedParts.value.join(", ") || props.source.label;
  return `everyone who can open ${slugs.map((s) => props.names[s] || s).join(", ")} (${who}) may see ${what}, and already has that access in ${props.source.label}`;
});

async function save(): Promise<void> {
  const slugs = formFor.value;
  if (!slugs?.length || !owner.value.trim() || saving.value) return;
  const ok = await confirm({
    title: "Record the data owner's confirmation?",
    message: `This records that ${owner.value.trim()} has confirmed that ${statement.value}. It stops counting if who can open the assistant, or what it reads here, changes.`,
    confirmLabel: "Record",
  });
  if (!ok) return;
  saving.value = true;
  let done = 0;
  try {
    for (const slug of slugs) {
      const updated = await attestGovernanceAccess(
        slug,
        {
          data_owner: owner.value.trim(),
          note: note.value.trim(),
          source_key: props.source.key,
        },
        props.devUser,
      );
      emit("gov-updated", updated);
      done += 1;
    }
    toast.success(
      done === 1 ? "Confirmation recorded" : `${done} confirmations recorded`,
    );
    formFor.value = null;
  } catch (e) {
    toast.error(
      done ? `Recorded ${done}, then failed` : "Could not record it",
      e instanceof AdminError ? e.message : String(e),
    );
  } finally {
    saving.value = false;
  }
}

function openAssistant(slug: string): void {
  window.location.hash = adminLink("assistants", {
    assistant: slug,
    section: "access",
  });
}
</script>

<template>
  <section class="access">
    <div class="access__head">
      <span class="muted small">
        {{
          needing.length
            ? `${needing.length} ${needing.length === 1 ? "assistant needs" : "assistants need"} the data owner to confirm who sees a restricted part`
            : "Nothing waiting on the data owner"
        }}
      </span>
      <button
        v-if="needing.length > 1 && !formFor"
        class="btn"
        v-write
        @click="start(needing.map((r) => r.slug))"
      >
        Confirm all {{ needing.length }}
      </button>
    </div>

    <p class="muted small intro">
      Each part of this source has one rule for who sees it. Under each part,
      the assistants that read it — and who, through each of them, actually sees
      it.
    </p>

    <article v-for="p in parts" :key="p.key" class="part">
      <header class="part__head">
        <span class="part__kind">{{ p.kind }}</span>
        <span class="part__label">{{ p.label }}</span>
      </header>
      <div class="part__rule">
        <template v-if="p.rule === 'group'">
          <StatusChip tone="warn">{{ restrictedTo(p) }}</StatusChip>
          <span v-if="p.tokens.length" class="muted small">
            data class
            <code>{{ p.tokens.map(groupLabel).join(", ") }}</code></span
          >
        </template>
        <span v-else class="small">{{ RULE[p.rule] }}</span>
      </div>

      <ul v-if="p.readers.length" class="readers">
        <li v-for="r in p.readers" :key="r.slug" class="reader">
          <button class="link reader__name" @click="openAssistant(r.slug)">
            {{ r.name }}
          </button>
          <span class="muted small">opened by {{ r.opens || "—" }}</span>
          <span v-if="r.sees" class="small reader__sees">
            → sees it: {{ r.sees }}</span
          >
          <span v-if="r.confirm" class="reader__conf small">
            <template v-if="r.conf.kind === 'current'">
              <StatusChip tone="ok">Data owner confirmed</StatusChip>
              <span class="muted">{{ r.conf.who }}, {{ r.conf.when }}</span>
            </template>
            <template v-else-if="r.conf.kind === 'stale'">
              <StatusChip tone="warn">Who can open it changed</StatusChip>
              <button v-write v-if="!formFor" class="link" @click="start([r.slug])">
                confirm again
              </button>
            </template>
            <template v-else-if="r.conf.kind === 'missing'">
              <button v-write v-if="!formFor" class="btn" @click="start([r.slug])">
                Confirm
              </button>
              <StatusChip v-else tone="warn">Not confirmed</StatusChip>
            </template>
          </span>
        </li>
      </ul>
      <p v-else class="muted small">No assistant reads this part.</p>
    </article>

    <form v-write v-if="formFor" class="confirm-form" @submit.prevent="save">
      <p class="small">
        The data owner confirms that <strong>{{ statement }}</strong
        >.
        <template v-if="formFor.length > 1">
          Recorded for {{ formFor.length }} assistants.</template
        >
      </p>
      <p class="small muted">
        This covers {{ source.label }} only. Anything else these assistants read
        is confirmed by its own data owner, on its own data source.
      </p>
      <label>
        Data owner
        <input
          v-model="owner"
          maxlength="200"
          placeholder="Name and role"
          required
        />
      </label>
      <label>
        Note
        <input
          v-model="note"
          maxlength="500"
          placeholder="Where they confirmed it"
        />
      </label>
      <div class="confirm-form__actions">
        <button type="button" class="btn btn--ghost" @click="formFor = null">
          Cancel
        </button>
        <button
          type="submit"
          class="btn btn--primary"
          :disabled="saving || !owner.trim()"
        >
          {{ saving ? "Recording…" : "Record confirmation" }}
        </button>
      </div>
    </form>
  </section>
</template>

<style scoped>
.access {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.access__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}
.access__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.access__table th {
  text-align: left;
  font-weight: 500;
  font-size: 0.75rem;
  color: var(--color-muted);
  padding: 0.25rem 0.6rem 0.25rem 0;
  border-bottom: 1px solid var(--color-border);
}
.access__table td {
  padding: 0.35rem 0.6rem 0.35rem 0;
  vertical-align: top;
  border-bottom: 1px solid var(--color-border);
}
.confirm-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 0.75rem;
  padding: 0.6rem 0.7rem;
  border-radius: var(--radius-sm);
  background: var(--color-surface-alt);
  font-size: 0.85rem;
}
.confirm-form p {
  grid-column: 1 / -1;
  margin: 0;
}
.confirm-form label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.confirm-form__actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.muted {
  color: var(--color-muted);
}
.warn-text {
  color: var(--color-warn-ink);
}
.small {
  font-size: 0.82rem;
}
button.link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: inherit;
  padding: 0;
  text-align: left;
}
.intro {
  margin: 0;
}
.part {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}
.part__head {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}
.part__kind {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-muted);
}
.part__label {
  font-weight: 600;
}
.part__rule {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  flex-wrap: wrap;
}
.readers {
  list-style: none;
  margin: 0.2rem 0 0;
  padding: 0 0 0 0.8rem;
  border-left: 2px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.reader {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.6rem;
  align-items: baseline;
}
.reader__conf {
  margin-left: auto;
  display: inline-flex;
  gap: 0.4rem;
  align-items: baseline;
}
</style>
